import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import LogoChurch from '../../assets/images/icons/logo-church.png';

import './Header.scss';

const Header = ({ transparent = false }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isLanguageOpen, setIsLanguageOpen] = useState(false);
    const [isResourcesOpen, setIsResourcesOpen] = useState(false);
    const location = useLocation();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close drawer on route change
    useEffect(() => {
        setIsDrawerOpen(false);
    }, [location.pathname]);

    // Prevent body scroll when drawer is open
    useEffect(() => {
        if (isDrawerOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isDrawerOpen]);

    const changeLanguage = useCallback((lang) => {
        i18n.changeLanguage(lang);
        setIsLanguageOpen(false);
    }, [i18n]);

    const toggleDrawer = useCallback(() => {
        setIsDrawerOpen(prev => !prev);
    }, []);

    const closeDrawer = useCallback(() => {
        setIsDrawerOpen(false);
    }, []);

    const currentLang = i18n.language === 'uk' ? 'УКР' : 'EN';

    return (
        <>
            <header className={`header ${(!transparent || isScrolled) ? 'header--scrolled' : ''}`}>
                <div className="header__container">
                    <div className="header__inner">
                        {/* Logo */}
                        <Link to="/" className="header__logo" aria-label={t('home')}>
                            <img
                                src={LogoChurch}
                                alt="Regeneration Church"
                                className="header__logo-img"
                            />
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className="header__nav" aria-label="Main navigation">
                            <ul className="header__list">
                                <li>
                                    <Link
                                        to="/"
                                        className={`header__link ${location.pathname === '/' ? 'header__link--active' : ''}`}
                                    >
                                        {t('home')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/about"
                                        className={`header__link ${location.pathname === '/about' ? 'header__link--active' : ''}`}
                                    >
                                        {t('about_us')}
                                    </Link>
                                </li>

                                {/* Resources Dropdown */}
                                <li
                                    className="header__dropdown-wrap"
                                    onMouseEnter={() => setIsResourcesOpen(true)}
                                    onMouseLeave={() => setIsResourcesOpen(false)}
                                >
                                    <button
                                        className="header__link header__link--dropdown"
                                        aria-expanded={isResourcesOpen}
                                        aria-haspopup="true"
                                        onClick={() => setIsResourcesOpen(prev => !prev)}
                                        type="button"
                                    >
                                        {t('resources')}
                                        <svg
                                            className={`header__arrow ${isResourcesOpen ? 'header__arrow--open' : ''}`}
                                            viewBox="0 0 24 24"
                                            width="12"
                                            height="12"
                                            aria-hidden="true"
                                        >
                                            <path d="M7 10l5 5 5-5z" fill="currentColor" />
                                        </svg>
                                    </button>

                                    {isResourcesOpen && (
                                        <div className="header__dropdown-menu" role="menu">
                                            <Link
                                                to="/catechism-ua"
                                                className="header__dropdown-item"
                                                role="menuitem"
                                            >
                                                {t('catechism_ua')}
                                            </Link>
                                            <Link
                                                to="/catechism-ru"
                                                className="header__dropdown-item"
                                                role="menuitem"
                                            >
                                                {t('catechism_ru')}
                                            </Link>
                                        </div>
                                    )}
                                </li>

                                <li>
                                    <Link
                                        to="/contact"
                                        className={`header__link ${location.pathname === '/contact' ? 'header__link--active' : ''}`}
                                    >
                                        {t('contacts')}
                                    </Link>
                                </li>

                                {/* Language Switcher */}
                                <li
                                    className="header__dropdown-wrap"
                                    onMouseEnter={() => setIsLanguageOpen(true)}
                                    onMouseLeave={() => setIsLanguageOpen(false)}
                                >
                                    <button
                                        className="header__link header__link--dropdown"
                                        aria-expanded={isLanguageOpen}
                                        aria-haspopup="true"
                                        onClick={() => setIsLanguageOpen(prev => !prev)}
                                        type="button"
                                    >
                                        {currentLang}
                                        <svg
                                            className={`header__arrow ${isLanguageOpen ? 'header__arrow--open' : ''}`}
                                            viewBox="0 0 24 24"
                                            width="12"
                                            height="12"
                                            aria-hidden="true"
                                        >
                                            <path d="M7 10l5 5 5-5z" fill="currentColor" />
                                        </svg>
                                    </button>

                                    {isLanguageOpen && (
                                        <div className="header__dropdown-menu" role="menu">
                                            <button
                                                className="header__dropdown-item"
                                                role="menuitem"
                                                onClick={() => changeLanguage('uk')}
                                                type="button"
                                            >
                                                Українська
                                            </button>
                                            <button
                                                className="header__dropdown-item"
                                                role="menuitem"
                                                onClick={() => changeLanguage('en')}
                                                type="button"
                                            >
                                                English
                                            </button>
                                        </div>
                                    )}
                                </li>
                            </ul>

                            {/* Donate Button (desktop) */}
                            <a
                                href="https://usaregeneration.churchcenter.com/giving"
                                className="button button--donate"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t('donate')}
                            </a>
                        </nav>

                        {/* Hamburger (mobile) */}
                        <button
                            className="header__hamburger"
                            onClick={toggleDrawer}
                            aria-label={t('aria_open_menu')}
                            aria-expanded={isDrawerOpen}
                            type="button"
                        >
                            <span className="header__hamburger-line" />
                            <span className="header__hamburger-line" />
                            <span className="header__hamburger-line" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Drawer Overlay */}
            {isDrawerOpen && (
                <div
                    className="header__overlay"
                    onClick={closeDrawer}
                    aria-hidden="true"
                />
            )}

            {/* Mobile Drawer */}
            <aside
                className={`header__drawer ${isDrawerOpen ? 'header__drawer--open' : ''}`}
                aria-label="Mobile navigation"
            >
                <div className="header__drawer-top">
                    <button
                        className="header__drawer-close"
                        onClick={closeDrawer}
                        aria-label={t('aria_close_menu')}
                        type="button"
                    >
                        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
                        </svg>
                    </button>
                </div>

                <nav className="header__drawer-nav">
                    <Link to="/" className="header__drawer-link" onClick={closeDrawer}>
                        {t('home')}
                    </Link>
                    <Link to="/about" className="header__drawer-link" onClick={closeDrawer}>
                        {t('about_us')}
                    </Link>
                    <Link to="/catechism-ua" className="header__drawer-link" onClick={closeDrawer}>
                        {t('catechism_ua')}
                    </Link>
                    <Link to="/catechism-ru" className="header__drawer-link" onClick={closeDrawer}>
                        {t('catechism_ru')}
                    </Link>
                    <Link to="/contact" className="header__drawer-link" onClick={closeDrawer}>
                        {t('contacts')}
                    </Link>
                    <Link to="/join" className="header__drawer-link" onClick={closeDrawer}>
                        {t('join_in_person')}
                    </Link>
                </nav>

                {/* Language Switcher (drawer) */}
                <div className="header__drawer-lang">
                    <button
                        className={`header__drawer-lang-btn ${i18n.language === 'en' ? 'header__drawer-lang-btn--active' : ''}`}
                        onClick={() => changeLanguage('en')}
                        type="button"
                    >
                        EN
                    </button>
                    <button
                        className={`header__drawer-lang-btn ${i18n.language === 'uk' ? 'header__drawer-lang-btn--active' : ''}`}
                        onClick={() => changeLanguage('uk')}
                        type="button"
                    >
                        УКР
                    </button>
                </div>

                {/* Donate (drawer) */}
                <a
                    href="https://usaregeneration.churchcenter.com/giving"
                    className="button button--donate header__drawer-donate"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t('donate')}
                </a>
            </aside>
        </>
    );
};

export default Header;
